import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, Languages, Loader2, MessageCircle, RefreshCw } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import ImageLightbox from '../../../components/ImageLightbox'
import { supabase } from '../../../lib/supabaseClient'
import { useRedditComments } from '../../../hooks/useNews'
import { cn } from '../../../lib/utils'

// Comments tree for a Reddit post on the article detail page. Calls
// the fetch-reddit-comments edge function once when the user opens
// the comments section. Renders up to 20 top-level threads, each
// expanded by default to depth 3.
//
// Auto-loads on first mount when articleLink is a Reddit URL — the
// user already chose to open this page, so making them tap a second
// button to see comments would be friction without an upside.
export default function RedditCommentsList({ articleLink, articleId, defaultOpen = true }) {
  const { t, i18n } = useTranslation()
  const {
    comments,
    loading,
    error,
    loaded,
    cached,
    fetchedAt,
    translations: serverTranslations,
    load,
    isReddit,
  } = useRedditComments(articleLink, articleId)
  const [open, setOpen] = useState(defaultOpen)
  const uiLang = i18n.language === 'ar' ? 'ar' : 'en'
  // Inline image / GIF lightbox state. Tapping any media in any
  // comment opens the lightbox at that image; the user can swipe
  // between images within the SAME comment.
  const [lightbox, setLightbox] = useState(null)
  // id → translated body map. Stays empty until the user taps
  // "ترجم"; once populated, the renderer below swaps body for the
  // translation when showTranslation is on.
  const [translatedById, setTranslatedById] = useState(() => new Map())
  const [showTranslation, setShowTranslation] = useState(false)
  const [translating, setTranslating] = useState(false)
  const [translateError, setTranslateError] = useState(null)

  // Trigger one load when this card becomes visible. Subsequent
  // toggles of `open` don't re-fetch — the user can refresh the
  // whole article page if they want fresh comments.
  useEffect(() => {
    if (!isReddit || !open || loaded || loading) return
    load('top')
  }, [isReddit, open, loaded, loading, load])

  // Flatten the tree to (id, body) pairs in render order. Used to
  // build the translation batch — translate-text takes a flat array
  // and returns translations in the same order, so we need a stable
  // index list to scatter results back.
  const flatComments = useMemo(() => {
    const out = []
    const walk = (list) => {
      for (const c of list) {
        if (c?.body) out.push({ id: c.id, body: c.body })
        if (Array.isArray(c?.replies) && c.replies.length) walk(c.replies)
      }
    }
    walk(comments)
    return out
  }, [comments])

  // Hydrate from server-cached translations if present. The cache
  // stores translations keyed by target language; if the user
  // already translated this post into the current UI language on a
  // previous open, we restore the map without re-paying for
  // Microsoft Translator.
  useEffect(() => {
    if (!serverTranslations || !flatComments.length) return
    const arr = serverTranslations[uiLang]
    if (!Array.isArray(arr) || !arr.length) return
    const next = new Map()
    for (let i = 0; i < flatComments.length && i < arr.length; i++) {
      const tx = arr[i]
      if (tx && typeof tx === 'string') next.set(flatComments[i].id, tx)
    }
    if (next.size > 0) setTranslatedById(next)
  }, [serverTranslations, flatComments, uiLang])

  const handleTranslate = useCallback(async () => {
    if (translating) return
    // Toggle off: don't re-fetch on next on — keep the cached map
    // and just hide it.
    if (showTranslation) {
      setShowTranslation(false)
      return
    }
    if (translatedById.size > 0) {
      setShowTranslation(true)
      return
    }
    if (!flatComments.length) return
    setTranslating(true)
    setTranslateError(null)
    try {
      const { data, error: err } = await supabase.functions.invoke('translate-text', {
        body: { strings: flatComments.map((c) => c.body), target_lang: uiLang },
      })
      if (err) throw err
      const next = new Map()
      const arr = Array.isArray(data?.translations) ? data.translations : []
      for (let i = 0; i < flatComments.length; i++) {
        const tx = arr[i]
        if (tx && typeof tx === 'string') next.set(flatComments[i].id, tx)
      }
      setTranslatedById(next)
      setShowTranslation(true)
      // Persist translations into the comment cache so a future open
      // of this post brings them back without paying Microsoft again.
      // Best-effort — if it fails, the user only loses the cross-
      // session reuse, not the current view.
      if (articleId && arr.length) {
        const prev = serverTranslations || {}
        const nextTranslations = { ...prev, [uiLang]: arr }
        supabase
          .from('news_reddit_comments')
          .update({ translations: nextTranslations })
          .eq('article_id', articleId)
          .then(() => {})
      }
    } catch (e) {
      setTranslateError(e?.message || 'failed')
    } finally {
      setTranslating(false)
    }
  }, [translating, showTranslation, translatedById, flatComments, uiLang, articleId, serverTranslations])

  // Force a live re-fetch from Reddit, bypassing the cache. Clears
  // the local translation state since the comment bodies may now
  // be different.
  const handleRefresh = useCallback(() => {
    setTranslatedById(new Map())
    setShowTranslation(false)
    load('top', { force: true })
  }, [load])

  if (!isReddit) return null

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-foreground flex-1 min-w-0"
            aria-expanded={open}
            dir="auto"
          >
            <MessageCircle className="w-4 h-4 text-app-primary-500" />
            <span>{t('news.reddit.commentsTitle')}</span>
            {loaded && comments.length > 0 && (
              <span className="text-[11px] text-muted-foreground font-mono" dir="ltr">
                ({comments.length})
              </span>
            )}
          </button>
          {open && loaded && comments.length > 0 && (
            <button
              type="button"
              onClick={handleTranslate}
              disabled={translating}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold transition-colors',
                showTranslation
                  ? 'bg-app-primary-500/10 text-app-primary-500'
                  : 'text-muted-foreground hover:bg-accent',
                translating && 'opacity-60',
              )}
              dir="auto"
              aria-pressed={showTranslation}
            >
              {translating ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Languages className="w-3 h-3" />
              )}
              {showTranslation
                ? t('news.reddit.showOriginal')
                : t('news.reddit.translateAll')}
            </button>
          )}
          {/* Refresh button — bypasses the 30-min cache and pulls a
              live copy of the comments tree from Reddit. Hidden until
              comments load so it doesn't dangle in empty state. */}
          {open && loaded && (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-60"
              aria-label={t('news.reddit.refreshComments')}
              title={t('news.reddit.refreshComments')}
            >
              <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
            </button>
          )}
          <span className="text-xs text-muted-foreground">
            {open ? t('common.hide') : t('common.show')}
          </span>
        </div>

        {/* "fetched N ago" hint — only when we know the result came
            from the cache. Quiet sub-row under the header. */}
        {open && cached && fetchedAt && (
          <p className="text-[10px] text-muted-foreground/80" dir="auto">
            {t('news.reddit.cachedHint', { time: relativeTimeAgo(fetchedAt, i18n.language) })}
          </p>
        )}

        {open && (
          <>
            {loading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-3" dir="auto">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>{t('news.reddit.commentsLoading')}</span>
              </div>
            )}
            {error && (
              <p className="text-xs text-red-600" dir="auto">
                {t('news.reddit.commentsError', { error })}
              </p>
            )}
            {translateError && (
              <p className="text-xs text-red-600" dir="auto">
                {t('news.reddit.translateError', { error: translateError })}
              </p>
            )}
            {loaded && comments.length === 0 && !error && (
              <p className="text-xs text-muted-foreground" dir="auto">
                {t('news.reddit.commentsEmpty')}
              </p>
            )}
            {loaded && comments.length > 0 && (
              // Top-level threads need more breathing room than the
              // intra-thread spacing so the user can tell where one
              // independent conversation ends and the next begins.
              <div className="space-y-5">
                {comments.map((c) => (
                  <CommentNode
                    key={c.id}
                    comment={c}
                    translatedById={translatedById}
                    showTranslation={showTranslation}
                    threadDir={null /* root: derive from body */}
                    onMediaClick={(images, index) => setLightbox({ images, index })}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
      <ImageLightbox state={lightbox} onClose={() => setLightbox(null)} />
    </Card>
  )
}

// Per-depth border colour. Rotates through four hues so the eye can
// visually track which thread a deeply-nested reply belongs to —
// the previous design used a single muted grey at every level,
// which made it hard to tell at a glance whether you were reading
// a reply to "alice" or "bob" two levels down.
//
// Hardcoded class strings (not template-built) so Tailwind's
// content scanner picks them up during the production build.
const THREAD_BORDERS = [
  'border-orange-500/60',  // depth 1
  'border-sky-500/60',     // depth 2
  'border-violet-500/60',  // depth 3
  'border-emerald-500/60', // depth 4+ (cap at 4 via mod)
]

// Recursive comment renderer. Depth-based start-padding + a coloured
// border-line per depth gives the "thread" feel without nesting a
// real tree component. Comments beyond depth 3 are dropped server-
// side, so we won't recurse deeper than that.
//
// threadDir is set at the top-level node from its body content and
// passed down through every reply. We do this so the indent +
// border guides ALWAYS go to the same side within a thread,
// matching the dominant text direction — otherwise an English
// (LTR) reply tree under an Arabic (RTL) UI would have its
// indentation guide appear on the wrong side, and replies would
// visually align with their parent instead of stepping under it.
function CommentNode({ comment, translatedById, showTranslation, threadDir, onMediaClick }) {
  const translated = showTranslation ? translatedById?.get(comment.id) : null
  const displayBody = translated || comment.body
  const bodyDir = isArabic(displayBody) ? 'rtl' : 'ltr'
  // The root node derives the thread direction from its own body;
  // descendants inherit. This means a mixed-language thread (rare)
  // still indents consistently rather than zig-zagging.
  const effectiveThreadDir = threadDir || bodyDir
  const isReply = comment.depth > 0
  const borderClass = isReply
    ? THREAD_BORDERS[(comment.depth - 1) % THREAD_BORDERS.length]
    : ''
  return (
    <div
      dir={effectiveThreadDir}
      className={cn(
        'text-xs',
        // Depth > 0: thicker (border-s-2) coloured guide line + wider
        // indent (ps-4) so the bar is visible *and* the content sits
        // away from it for breathing room. With dir set above,
        // border-s + ps-4 resolve to the correct side regardless of
        // the surrounding document direction.
        isReply && 'border-s-2 ps-4',
        borderClass,
      )}
    >
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1" dir="ltr">
        <span className={cn('font-medium', comment.is_op && 'text-app-primary-500')}>
          u/{comment.author}
        </span>
        {comment.is_op && (
          <span className="px-1 py-0.5 rounded bg-app-primary-500/10 text-app-primary-500 text-[9px] font-bold">
            OP
          </span>
        )}
        {comment.distinguished === 'moderator' && (
          <span className="px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-600 text-[9px] font-bold">
            MOD
          </span>
        )}
        <span className="inline-flex items-center gap-0.5 font-mono">
          <ArrowUp className="w-2.5 h-2.5 text-orange-500" />
          {formatScore(comment.score)}
        </span>
      </div>
      {displayBody && (
        <p className="text-foreground/85 leading-relaxed whitespace-pre-wrap" dir={bodyDir}>
          {displayBody}
        </p>
      )}
      {/* Inline GIFs / images extracted from the comment's body_html.
          Tap to open in the parent's lightbox — multiple images in
          the same comment become swipeable. Falls back to opening
          in a new tab when onMediaClick isn't supplied. */}
      {Array.isArray(comment.media) && comment.media.length > 0 && (
        <div className={cn('flex flex-wrap gap-2', displayBody ? 'mt-2' : '')}>
          {comment.media.map((m, idx) => {
            const urls = comment.media.map((mm) => mm.url)
            const open = (e) => {
              if (!onMediaClick) return // fall through to <a> href
              e.preventDefault()
              e.stopPropagation()
              onMediaClick(urls, idx)
            }
            return (
              <a
                key={`${m.url}-${idx}`}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={open}
                className="block rounded-md overflow-hidden bg-muted max-w-[200px] cursor-zoom-in"
              >
                <img
                  src={m.url.replace(/&amp;/g, '&')}
                  alt={m.kind === 'gif' ? 'GIF' : 'image'}
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  className="max-h-[200px] w-auto object-contain"
                  onError={(e) => { e.currentTarget.parentElement.style.display = 'none' }}
                />
              </a>
            )
          })}
        </div>
      )}
      {Array.isArray(comment.replies) && comment.replies.length > 0 && (
        <div className="mt-3 space-y-3">
          {comment.replies.map((r) => (
            <CommentNode
              key={r.id}
              comment={r}
              translatedById={translatedById}
              showTranslation={showTranslation}
              threadDir={effectiveThreadDir}
              onMediaClick={onMediaClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function formatScore(n) {
  if (!Number.isFinite(n)) return '0'
  const abs = Math.abs(n)
  if (abs < 1000) return String(n)
  if (abs < 10000) {
    const k = n / 1000
    return `${k.toFixed(1).replace(/\.0$/, '')}K`
  }
  return `${Math.round(n / 1000)}K`
}

// Short "N min ago / N hours ago" formatter for the cache-age hint.
// Mirrors timeAgo in NewsPage but localised to ar/en here so this
// component doesn't have to import that file.
function relativeTimeAgo(iso, lang) {
  if (!iso) return ''
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const ar = lang === 'ar'
  const mins = Math.floor((Date.now() - t) / 60000)
  if (mins < 1) return ar ? 'الآن' : 'just now'
  if (mins < 60) return ar ? `${mins} د` : `${mins} min ago`
  const hours = Math.floor(mins / 60)
  return ar ? `${hours} س` : `${hours} h ago`
}

function isArabic(s) {
  if (!s) return false
  let a = 0
  let l = 0
  for (const ch of s) {
    const c = ch.codePointAt(0)
    if (!c) continue
    if (c >= 0x0600 && c <= 0x06FF) a += 1
    else if ((c >= 0x41 && c <= 0x5A) || (c >= 0x61 && c <= 0x7A)) l += 1
  }
  return a > l
}
